/**
 * useMailshotsData Hook
 *
 * Manages mailshots data including:
 * - Fetching mailshots
 * - Creating mailshots
 * - Adding properties to mailshots
 */

import { useState, useCallback, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
  writeBatch,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../components/shared";

export const useMailshotsData = () => {
  const toast = useToast();
  const [mailshots, setMailshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Fetch mailshots
  const fetchMailshots = useCallback(async () => {
    try {
      setLoading(true);
      const mailshotsQuery = query(
        collection(db, "discovery_mailshots"),
        orderBy("createdAt", "desc")
      );
      const mailshotsSnapshot = await getDocs(mailshotsQuery);
      const mailshotsData = mailshotsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMailshots(mailshotsData);
      return mailshotsData;
    } catch (error) {
      console.error("Error fetching mailshots:", error);
      // If orderBy fails due to missing index, try without orderBy
      try {
        const mailshotsSnapshot = await getDocs(
          collection(db, "discovery_mailshots")
        );
        const mailshotsData = mailshotsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMailshots(mailshotsData);
        return mailshotsData;
      } catch (fallbackError) {
        console.error("Error fetching mailshots (fallback):", fallbackError);
        toast.error("Failed to load mailshots");
        return [];
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Delete a mailshot and clean up references
   * - Removes mailshot document
   * - Removes mailshotId from all properties that were in this mailshot
   * - When campaigns are implemented, will also update campaign
   */
  const deleteMailshot = useCallback(
    async (mailshotId) => {
      try {
        setDeleting(mailshotId);

        // Get the mailshot to find which properties are in it
        const mailshotToDelete = mailshots.find((m) => m.id === mailshotId);
        if (!mailshotToDelete) {
          toast.error("Mailshot not found");
          return false;
        }

        const batch = writeBatch(db);

        // 1. Delete the mailshot document
        batch.delete(doc(db, "discovery_mailshots", mailshotId));

        // 2. Remove mailshotId from all properties in this mailshot
        if (
          mailshotToDelete.propertyIds &&
          mailshotToDelete.propertyIds.length > 0
        ) {
          for (const propertyId of mailshotToDelete.propertyIds) {
            const propertyRef = doc(db, "discovery_properties", propertyId);
            batch.update(propertyRef, {
              mailshotIds: arrayRemove(mailshotId),
            });
          }
        }

        // 3. TODO: When campaigns are implemented, update campaign stats
        // if (mailshotToDelete.campaignId) {
        //   const campaignRef = doc(db, "campaigns", mailshotToDelete.campaignId);
        //   batch.update(campaignRef, {
        //     mailshotIds: arrayRemove(mailshotId),
        //   });
        // }

        // Commit the batch
        await batch.commit();

        // Update local state
        setMailshots((prev) => prev.filter((m) => m.id !== mailshotId));
        toast.success("Mailshot deleted successfully");
        return true;
      } catch (error) {
        console.error("Error deleting mailshot:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can delete mailshots.");
        } else {
          toast.error("Failed to delete mailshot: " + error.message);
        }
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [mailshots, toast]
  );

  // Initial fetch
  useEffect(() => {
    fetchMailshots();
  }, [fetchMailshots]);

  return {
    mailshots,
    loading,
    deleting,
    fetchMailshots,
    deleteMailshot,
  };
};
